import React, { useState } from 'react';

import CentralizedSpinner from 'components/CentralizedSpinner';
import FullscreenChart from 'components/Layout/MobileShell/FullscreenChart';
import useTabData from 'hooks/Windx/About/useTabData';
import useInstructionPdf from 'hooks/Windx/About/useInstructionPdf';
import useDragScroll from 'utils/hooks/useDragScroll';

import './index.scss';

/**
 * Sobre o Windx no mobile (spec Windx mobile, Task 14 / D9).
 *
 * As abas vem da API (`useTabData`), nao de rota — entao `SectionTabs`, que e
 * route-driven, nao serve. Fileira de chips com `useDragScroll`, no mesmo
 * padrao visual.
 *
 * O `<object>` do PDF so existe em tela cheia: embutido num card de 390px ele
 * fica ilegivel e ainda concorre com o scroll da pagina. O overlay reusado e o
 * `MobileShell/FullscreenChart` — o nome diz "chart", mas ele e generico
 * (cabecalho com titulo + fechar e children ocupando a tela).
 *
 * "Baixar" e uma ancora direta: o endpoint devolve URL assinada (PAR 24h) do
 * Object Storage, entao nao ha blob nosso para `useFileDownloadApi` entregar.
 */
export default function AboutMobile() {
  const { instructionTabs, isLoading } = useTabData();
  const [abaSelecionada, setAbaSelecionada] = useState<number | null>(null);
  const [telaCheia, setTelaCheia] = useState(false);
  const {
    ref,
    dragHandlers: {
      onPointerDown, onPointerMove, onPointerUp, onPointerLeave, onClickCapture,
    },
  } = useDragScroll<HTMLDivElement>();

  const abas = instructionTabs ?? [];
  const idAtivo = abaSelecionada ?? abas[0]?.id ?? null;
  const abaAtiva = abas.find((t) => t.id === idAtivo);
  const { pdfUrl } = useInstructionPdf(idAtivo ?? 0, idAtivo !== null);

  return (
    <div className="windx-about-mobile" data-testid="windx-about-mobile">
      <h1 className="windx-about-mobile-titulo">Sobre</h1>

      {isLoading && <CentralizedSpinner />}

      {abas.length > 0 && (
        <>
          <div
            className="windx-about-mobile-chips"
            role="tablist"
            aria-label="Seções do Sobre"
            ref={ref}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerLeave}
            onClickCapture={onClickCapture}
          >
            {abas.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={tab.id === idAtivo}
                className={`windx-about-mobile-chip${tab.id === idAtivo ? ' is-ativo' : ''}`}
                onClick={() => setAbaSelecionada(tab.id)}
              >
                {tab.title}
              </button>
            ))}
          </div>

          <div className="windx-about-mobile-card">
            <p className="windx-about-mobile-card-titulo">{abaAtiva?.title}</p>
            <p className="windx-about-mobile-card-dica">
              Documento em PDF. Abra em tela cheia para ler ou baixe para consultar depois.
            </p>

            <div className="windx-about-mobile-acoes">
              <button
                type="button"
                className="windx-about-mobile-acao is-primaria"
                onClick={() => setTelaCheia(true)}
                disabled={!pdfUrl}
              >
                Ler em tela cheia
              </button>
              <a
                className="windx-about-mobile-acao"
                href={pdfUrl || undefined}
                download
                target="_blank"
                rel="noreferrer"
              >
                Baixar
              </a>
            </div>
          </div>
        </>
      )}

      <FullscreenChart
        open={telaCheia}
        dismiss={() => setTelaCheia(false)}
        title={abaAtiva?.title}
      >
        {telaCheia && pdfUrl && (
          <object
            title={`${abaAtiva?.title} PDF`}
            data={pdfUrl}
            className="windx-about-mobile-pdf"
          />
        )}
      </FullscreenChart>
    </div>
  );
}
